<template>
  <div
    v-if="state.cashAccounts?.length === 1"
    class="flex h-full items-center gap-4 px-3 py-1 text-yapgray-600 dark:text-yapgray-200"
  >
    <YapIcon name="coins-outline" class="size-8" />
    <div>
      <div class="text-xs text-yapgray-500 dark:text-yapgray-200">
        {{ props.title ?? t('app.payments.account_used.txt') }}
      </div>
      <div class="flex items-center justify-between">
        <div class="truncate dark:text-primary-dark-100">
          {{ state.cashAccounts[0].accountName }}
          <span class="text-xs text-yapgray-500 dark:text-yapgray-600">
            {{ t('app.payments.availableBalance') }}
            <YapAmount
              :amount="state.cashAccounts[0].totalValuation"
              :currency="state.cashAccounts[0].currency"
              :show-positive-sign="false"
              :show-negative-sign="true"
              class="text-yapgray-800 dark:text-yapgray-200"
            />
          </span>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <select
      v-if="simpleSelect"
      name="accountUid"
      id="accountUid"
      v-model="modelValue"
      class="w-full border-yapgray-300 text-yapgray-800 focus:border-primary-500 focus:ring-primary-500 dark:focus:border-primary-dark-500 dark:focus:ring-primary-dark-500 dark:border-yapdarkgray-600 dark:bg-yapdarkgray-800 dark:text-yapgray-100"
      :class="{
        'rounded-lg shadow-sm': !grouped,
        'border-0': grouped,
        'h-14': floating,
        'pt-3.5 placeholder-transparent dark:placeholder-transparent': floating,
        'bg-yapgray-100 text-yapgray-600 dark:bg-yapdarkgray-900 dark:text-yapgray-600': readonly
      }"
    >
      <option
        v-for="account in state.cashAccounts"
        :key="account._links.self.uri"
        :value="getUidFromSelfLink(account._links.self)"
      >
        {{ account.accountName ?? '{?}' }}
      </option>
    </select>
    <YapListBox
      v-else-if="state.listBoxOptions"
      v-model="modelValue"
      :options="state.listBoxOptions"
      :grouped="grouped"
      :floating="floating"
      v-bind="$attrs"
    >
      <template #trigger="{ selectedOption }">
        <div class="relative">
          <div class="absolute -top-5 block h-24 text-xs text-yapgray-500">
            {{ props.title ?? t('app.payments.account_used.txt') }}
          </div>
          <div class="flex items-center justify-between">
            <div class="block truncate dark:text-primary-dark-100">
              {{ selectedOption?.name }}
              <span class="text-xs text-yapgray-500 dark:text-yapgray-600">
                {{ t('app.payments.availableBalance') }}
                <YapAmount
                  :amount="selectedOption?.extraData.totalValuation"
                  :currency="selectedOption?.extraData.currency"
                  :show-positive-sign="false"
                  :show-negative-sign="true"
                  class="text-yapgray-800 dark:text-yapgray-200"
                />
              </span>
            </div>
          </div>
        </div>
      </template>
      <template #option="{ option, selected }">
        <div class="flex items-center justify-between">
          <div>
            <div :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
              {{ option.name }}
            </div>
            <div class="text-xs text-yapgray-500 dark:text-yapgray-600">
              {{ t('app.payments.availableBalance') }}
              <YapAmount
                :amount="option.extraData.totalValuation"
                :currency="option.extraData.currency"
                :show-positive-sign="false"
                :show-negative-sign="true"
                class="text-yapgray-800 dark:text-yapgray-300"
              />
            </div>
          </div>
          <YapCurrencyIcon :currency="option.extraData.currency" class="size-4" />
        </div>
      </template>
    </YapListBox>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted } from 'vue';

import { useI18n } from '@/plugins/i18n';
import YapIcon from '@/components/YapIcon.vue';
import { CashAccount } from '@/types/cashAccount';
import YapAmount from '@/components/YapAmount.vue';
import { getUidFromSelfLink } from '@/utils/links';
import YapListBox from '@/components/YapListBox.vue';
import YapCurrencyIcon from '@/components/YapCurrencyIcon.vue';
import { YapListBoxOption } from '@/components/YapListBox.vue';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';

const modelValue = defineModel<string>();
const emit = defineEmits(['update:modelValue']);

const props = defineProps<{
  simpleSelect?: boolean;
  grouped?: boolean;
  floating?: boolean;
  readonly?: boolean;
  internationalOnly?: boolean;
  title?: string;
  stopPrefill?: boolean;
}>();

const { t } = useI18n();
const cashAccountsStore = useCashAccountsStore();
const contextSwitchStore = useContextSwitchStore();

interface State {
  cashAccounts?: Array<CashAccount>;
  listBoxOptions?: Array<YapListBoxOption>;
}
const state: State = reactive({
  cashAccounts: contextSwitchStore.currentContextUid
    ? props.internationalOnly
      ? cashAccountsStore.entitledActiveCashAccountsForCurrentContext.filter((a) => a.internationalPaymentsAvailable)
      : cashAccountsStore.entitledActiveCashAccountsForCurrentContext
    : undefined,
  listBoxOptions: computed(() => {
    return state.cashAccounts?.map((a) => {
      return {
        value: getUidFromSelfLink(a._links.self),
        name: a.accountName,
        extraData: {
          currency: a.currency,
          totalValuation: a.totalValuation,
          iban: a.iban
        }
      };
    });
  })
});

onMounted(() => {
  if (!props.stopPrefill && state.cashAccounts?.length) {
    emit('update:modelValue', cashAccountsStore.selectedCashAccountUid);
  }
});
</script>
